import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d6tg5ekj8d20juc903eg';

const interests = ['Living Room Furniture', 'Bedroom Furniture', 'Dining Room', 'Home Office', 'Outdoor Furniture', 'Full Room Design', 'Custom Order', 'General Inquiry'];

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const data = new FormData(e.currentTarget);
    const params = new URLSearchParams();
    data.forEach((v, k) => params.append(k, v.toString()));
    try {
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: params.toString() });
      if (res.ok) setSubmitted(true);
      else setError('Something went wrong. Please call or visit our showroom.');
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-w-[1024px]">
      <Navbar />
      <main>
        <section className="bg-dark pt-44 pb-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Let's Talk</span>
              <span className="w-8 h-px bg-gold" />
            </div>
            <h1 className="font-serif text-6xl font-bold text-white mb-5">Contact Us</h1>
            <p className="text-white/50 text-lg max-w-xl mx-auto">
              Ready to transform your home? Reach out to schedule your complimentary design consultation.
            </p>
          </div>
        </section>

        <section className="py-24 bg-cream-DEFAULT">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14">
            {/* Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-px bg-gold" />
                <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Get In Touch</span>
              </div>
              <h2 className="font-serif text-4xl font-bold text-dark mb-8 leading-tight">
                We'd Love to<br /><span className="text-gold">Hear From You</span>
              </h2>
              <div className="space-y-5 mb-10">
                {[
                  { icon: 'ri-map-pin-line', label: 'Showroom', val: '3750 S Las Vegas Blvd, Las Vegas, NV' },
                  { icon: 'ri-phone-line', label: 'Phone', val: '(702) 850-9900', href: 'tel:7028509900' },
                  { icon: 'ri-mail-line', label: 'Email', val: 'hello@eclectichome.com', href: 'mailto:hello@eclectichome.com' },
                  { icon: 'ri-time-line', label: 'Hours', val: 'Mon–Fri 10am–7pm · Sat 10am–6pm · Sun 11am–5pm' },
                ].map((c) => (
                  <div key={c.label} className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gold/10 flex-shrink-0">
                      <i className={`${c.icon} text-gold`} />
                    </div>
                    <div>
                      <span className="block text-[10px] text-dark/40 font-semibold uppercase tracking-wider mb-1">{c.label}</span>
                      {c.href ? (
                        <a href={c.href} className="text-dark font-semibold text-sm hover:text-gold transition-colors cursor-pointer">{c.val}</a>
                      ) : (
                        <span className="text-dark font-semibold text-sm">{c.val}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-cream-mid rounded-2xl p-7 border border-cream-dark">
                <h3 className="font-serif text-lg font-bold text-dark mb-3">Why Book a Consultation?</h3>
                {['Personalized design guidance', 'Access to exclusive fabrics and finishes', 'Room planning and layout advice', 'Budget-focused recommendations', 'No purchase obligation'].map((item) => (
                  <div key={item} className="flex items-center gap-3 py-2.5 border-b border-cream-dark last:border-0">
                    <i className="ri-check-line text-gold" />
                    <span className="text-dark/70 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="bg-dark rounded-2xl p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gold/20 mx-auto mb-5">
                    <i className="ri-check-line text-gold text-3xl" />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-white mb-3">Thank You!</h3>
                  <p className="text-white/50 text-sm">Our design team will be in touch within 24 hours to schedule your consultation.</p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <h3 className="font-serif text-xl font-bold text-white mb-1">Book Your Consultation</h3>
                    <p className="text-white/40 text-xs">Free · No obligation · 24hr response</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">First Name *</label>
                      <input name="firstName" type="text" required placeholder="Alexandra"
                        className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Last Name *</label>
                      <input name="lastName" type="text" required placeholder="Whitfield"
                        className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Email *</label>
                    <input name="email" type="email" required placeholder="hello@example.com"
                      className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Phone</label>
                    <input name="phone" type="tel" placeholder="(702) 555-0000"
                      className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">I'm Interested In *</label>
                    <select name="interest" required className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-gold transition-colors cursor-pointer">
                      <option value="" className="bg-dark">Select...</option>
                      {interests.map((i) => <option key={i} value={i} className="bg-dark">{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-wider mb-2">Message</label>
                    <textarea name="message" rows={3} maxLength={500}
                      placeholder="Tell us about your space and style goals..."
                      className="w-full bg-white/8 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-gold transition-colors resize-none" />
                  </div>
                  {error && <p className="text-red-400 text-xs">{error}</p>}
                  <button type="submit" disabled={loading}
                    className="w-full bg-gold text-white py-4 rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-gold-dark transition-colors cursor-pointer disabled:opacity-70 whitespace-nowrap">
                    {loading ? 'Sending...' : 'Book Free Consultation →'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="bg-cream-mid py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="w-full h-80 rounded-2xl overflow-hidden border border-cream-dark">
              <iframe
                title="Eclectic Home Furniture Store Las Vegas"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52577.69!2d-115.2!3d36.12!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80beb782a4f57dd1%3A0x3accd5e6d5b379a3!2sLas%20Vegas%2C%20NV!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
